import { useMemo } from 'react';
import PropTypes from 'prop-types';
import './GoldConfettiBurst.css';

const PARTICLES_PER_SOURCE = 60;
const COLORS = ['#fff6d8', '#f6dfa0', '#e8b84b', '#d99f2e', '#c9942b'];
const DEFAULT_SPREAD_DEG = 40;

const buildParticles = (seed, spreadDeg, waveDelay) => (
    Array.from({ length: PARTICLES_PER_SOURCE }, (_, i) => {
        const deg = -90 + (Math.random() * 2 - 1) * spreadDeg;
        const rad = (deg * Math.PI) / 180;
        const burstDistance = 260 + Math.random() * 320;
        const isRibbon = Math.random() < 0.35;
        return {
            id: `${seed}-${i}`,
            burstX: Math.cos(rad) * burstDistance,
            burstY: Math.sin(rad) * burstDistance,
            fallExtra: 110 + Math.random() * 150,
            isRibbon,
            width: isRibbon ? 5 + Math.random() * 3 : 3 + Math.random() * 3,
            height: isRibbon ? 8 + Math.random() * 5 : 3 + Math.random() * 3,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            duration: 2600 + Math.random() * 1400,
            delay: waveDelay + Math.random() * 200,
            spinX: Math.random() < 0.5 ? -1 : 1,
            spinZ: Math.random() < 0.5 ? -1 : 1,
            spinTurns: 2 + Math.random() * 3,
        };
    })
);

/**
 * 新卡片掛上樹的瞬間，從樹底部幾個噴發點向上噴出彩帶／金粉，模擬慶祝拉炮：
 * 噴出後帶拋物線墜落與翻轉，涵蓋整棵樹範圍而非只落在單張卡片上；
 * 混合小圓點（金粉）與小方塊（彩帶片），比純粒子飄落更有慶祝感。
 * waves：多波次噴發 [{ waveDelay, spreadDeg, sources: [{x,y}] }, ...]；
 * 各波角度範圍、點位、啟動時間皆可不同，接力補滿整棵樹範圍、看起來像連續施放。
 */
const GoldConfettiBurst = ({ waves }) => {
    const bursts = useMemo(() => (
        waves.flatMap((wave, wi) => (
            wave.sources.map((source, si) => ({
                ...source,
                particles: buildParticles(`${wi}-${si}`, wave.spreadDeg ?? DEFAULT_SPREAD_DEG, wave.waveDelay ?? 0),
            }))
        ))
    ), [waves]);

    return (
        <>
            {bursts.map((burst, bi) => (
                <div key={bi} className="gold-confetti-burst" style={{ left: `${burst.x}px`, top: `${burst.y}px` }}>
                    {burst.particles.map((p) => (
                        <span
                            key={p.id}
                            className={`gold-confetti-particle${p.isRibbon ? ' gold-confetti-particle--ribbon' : ''}`}
                            style={{
                                width: `${p.width}px`,
                                height: `${p.height}px`,
                                background: p.isRibbon
                                    ? p.color
                                    : `radial-gradient(circle, #fff6d8 0%, ${p.color} 65%, #e0a83e 100%)`,
                                animationDelay: `${p.delay}ms`,
                                animationDuration: `${p.duration}ms`,
                                '--burst-x': `${p.burstX}px`,
                                '--burst-y': `${p.burstY}px`,
                                '--fall-extra': `${p.fallExtra}px`,
                                '--spin-x': p.spinX,
                                '--spin-z': p.spinZ,
                                '--spin-turns': p.spinTurns,
                            }}
                        />
                    ))}
                </div>
            ))}
        </>
    );
};

GoldConfettiBurst.propTypes = {
    waves: PropTypes.arrayOf(PropTypes.shape({
        waveDelay: PropTypes.number,
        spreadDeg: PropTypes.number,
        sources: PropTypes.arrayOf(PropTypes.shape({
            x: PropTypes.number.isRequired,
            y: PropTypes.number.isRequired,
        })).isRequired,
    })).isRequired,
};

export default GoldConfettiBurst;
